import { sparkBurstAt } from './sparkEffect';

// GG Anvil edge-spark flourish (Phase 4b) — a brief, sequential trace of
// sparks around a section's edge, fired the moment the Setup checklist's
// "over here" mechanism (lib/sectionHighlight.tsx) actually lands a
// highlight. Layered ON TOP of that mechanism's existing scroll + pulse +
// auto-focus — this file only reads the target element's rect and calls the
// SAME brand-matched shard burst sparkEffect.ts already runs for clicks
// (sparkBurstAt), just at computed edge points instead of a pointerdown.
//
// "One after the other around the edge" (Lisa) — points are walked clockwise
// around the element's perimeter starting top-left, each firing a small
// burst STEP_MS after the last, fewer shards and no ring per point (vs. a
// real click) so a big section doesn't turn into a fireworks show.

const POINTS_AROUND = 10; // sparks per flourish — enough to read as a trace, not a ring of dots
const STEP_MS = 65; // gap between each point catching light
const EDGE_SCALE = 0.55; // smaller than a click-strike's 0.6–1.7 random range
const EDGE_SHARD_COUNT = 6; // vs. the click burst's 14 — a trace, not an explosion

let pendingTimers: number[] = [];

function clearPending() {
  pendingTimers.forEach(id => window.clearTimeout(id));
  pendingTimers = [];
}

// Walks `rect`'s perimeter clockwise from the top-left corner, returning
// `count` evenly-spaced points.
function pointAtDistance(rect: DOMRect, d: number): { x: number; y: number } {
  const { width: w, height: h, left, top } = rect;
  if (d < w) return { x: left + d, y: top }; // top edge, left → right
  d -= w;
  if (d < h) return { x: left + w, y: top + d }; // right edge, top → bottom
  d -= h;
  if (d < w) return { x: left + w - d, y: top + h }; // bottom edge, right → left
  d -= w;
  return { x: left, y: top + h - d }; // left edge, bottom → top
}

function pointsAroundRect(rect: DOMRect, count: number): { x: number; y: number }[] {
  const perimeter = 2 * (rect.width + rect.height);
  if (perimeter <= 0) return [];
  const points: { x: number; y: number }[] = [];
  for (let i = 0; i < count; i++) {
    points.push(pointAtDistance(rect, (perimeter * i) / count));
  }
  return points;
}

/**
 * Fire the edge-spark flourish around `el`'s current bounding box. Meant to
 * be called from useSectionHighlightController's onHighlight callback (see
 * TournamentSetup.tsx) — additive only, never call this in place of the
 * scroll/pulse/focus mechanism, only alongside it.
 *
 * No-ops under prefers-reduced-motion (sparkBurstAt itself also guards this,
 * but checking here too skips the setTimeout chain entirely rather than
 * scheduling N no-op calls) and clears any still-pending flourish from a
 * previous jump so two rapid checklist clicks don't stack traces.
 */
export function fireEdgeSparkFlourish(el: HTMLElement): void {
  clearPending();
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const rect = el.getBoundingClientRect();
  const points = pointsAroundRect(rect, POINTS_AROUND);
  points.forEach((p, i) => {
    const id = window.setTimeout(() => {
      sparkBurstAt(p.x, p.y, { scale: EDGE_SCALE, shardCount: EDGE_SHARD_COUNT, ring: false });
    }, i * STEP_MS);
    pendingTimers.push(id);
  });
}
